import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import DashboardCardIcon from '../components/icons/DashboardCardIcon';
import { useAuth } from '../contexts/AuthContext';
import { getApiBaseUrl } from '../utils/apiBaseUrl';

const TRAINING_BLUE = '#2196F3';

/* ── 특별수당 기준 안내 박스 ── */
function AllowanceGuide() {
  const [open, setOpen] = React.useState(false);
  const items = [
    {
      emoji: '🌙', title: '새벽출근수당', color: '#1e1b4b', text: '#c7d2fe',
      rules: [
        '오전 6시 5분 이전에 출근 체크 (정상 상태, 즉 사무실에서 체크시)',
        '또는 승인 상태로 오전 6시 5분 이전 출근 후, 1시간 이내 사무실 30km+ 또는 두 출근지 간 30km+ 재출근',
        '또는 승인 상태 새벽 출근 + 당일 80km+ 거리 퇴근(출장 패턴)',
      ],
    },
    {
      emoji: '🏨', title: '외박수당 1일', color: '#164e63', text: '#a5f3fc',
      rules: [
        '새벽출근수당이 발생한 날',
        '당일 퇴근이 사무실에서 80km 이상 거리에서 체크',
        '다음 날 오전 9시 이전에 사무실에서 80km 이상 거리에서 출근 체크',
      ],
    },
    {
      emoji: '🌙🌙', title: '연박수당', color: '#134e4a', text: '#99f6e4',
      rules: [
        '외박수당 또는 연박수당이 발생한 날에 이어서',
        '당일 퇴근이 사무실에서 80km 이상 거리에서 체크',
        '다음 날 오전 9시 이전에 사무실에서 80km 이상 거리에서 출근 체크',
        '조건 충족 시 연속으로 계속 연박수당 적용',
      ],
    },
  ];
  return (
    <div style={{ margin: '0 0 10px', borderRadius: 10, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
      <button type="button" onClick={() => setOpen(v => !v)} style={{
        width: '100%', padding: '8px 14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        background: '#f8fafc', border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 700, color: '#334155',
      }}>
        <span>💡 특별수당 발생 기준 안내</span>
        <span style={{ fontSize: 11, color: '#94a3b8' }}>{open ? '▲ 닫기' : '▼ 펼치기'}</span>
      </button>
      {open && (
        <div style={{ padding: '10px 14px', background: '#fff', display: 'flex', flexDirection: 'column', gap: 10 }}>
          {items.map(({ emoji, title, color, text, rules }) => (
            <div key={title} style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
              <span style={{
                flexShrink: 0, padding: '3px 10px', borderRadius: 99, fontSize: 12, fontWeight: 800,
                background: color, color: text, whiteSpace: 'nowrap', marginTop: 1,
              }}>{emoji} {title}</span>
              <ul style={{ margin: 0, padding: '0 0 0 16px', fontSize: 12, color: '#475569', lineHeight: 1.7 }}>
                {rules.map((r, i) => <li key={i}>{r}</li>)}
              </ul>
            </div>
          ))}
          <p style={{ margin: '4px 0 0', fontSize: 11, color: '#94a3b8' }}>
            ※ 수당 금액은 관리자가 설정한 기준에 따라 자동 계산됩니다.
          </p>
        </div>
      )}
    </div>
  );
}

function fmtNoticeDate(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('ko-KR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return String(iso).slice(0, 16);
  }
}

function AttendanceNoticeGateModal({ notice, confirming, requireCheck, onConfirm }) {
  useEffect(() => {
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, []);

  if (!notice) return null;

  return createPortal(
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 10000,
        background: 'rgba(15,23,42,0.62)',
        backdropFilter: 'blur(4px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 20,
      }}
      role="presentation"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="attendance-notice-title"
        style={{
          width: '100%',
          maxWidth: 420,
          maxHeight: '85vh',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          background: '#fff',
          borderRadius: 20,
          boxShadow: '0 20px 60px rgba(0,0,0,0.28)',
          animation: 'modalIn 0.22s ease',
        }}
      >
        <div style={{ padding: '22px 22px 14px', borderBottom: '1px solid #f1f5f9' }}>
          <div style={{ fontSize: 11, fontWeight: 800, color: '#dc2626', marginBottom: 8 }}>
            출퇴근 체크할 때마다 확인해 주세요
          </div>
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            padding: '3px 8px',
            borderRadius: 999,
            background: '#eff6ff',
            color: '#1d4ed8',
            fontSize: 11,
            fontWeight: 800,
            marginBottom: 10,
          }}
          >
            공지사항
          </div>
          <h3 id="attendance-notice-title" style={{ margin: 0, fontSize: 18, fontWeight: 900, color: '#0f172a', lineHeight: 1.4 }}>
            {notice.title}
          </h3>
          {notice.receivedAt ? (
            <p style={{ margin: '8px 0 0', fontSize: 12, color: '#64748b' }}>{fmtNoticeDate(notice.receivedAt)}</p>
          ) : null}
        </div>
        <div style={{ padding: '18px 22px', overflowY: 'auto', flex: 1 }}>
          <p style={{ margin: 0, fontSize: 14, color: '#334155', lineHeight: 1.7, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
            {notice.body || '(내용 없음)'}
          </p>
        </div>
        <div style={{ padding: '16px 22px 20px', borderTop: '1px solid #f1f5f9' }}>
          <button
            type="button"
            disabled={confirming}
            onClick={onConfirm}
            style={{
              width: '100%',
              padding: '14px 0',
              borderRadius: 14,
              border: 'none',
              background: confirming ? '#93c5fd' : 'linear-gradient(135deg,#1d4ed8,#2563eb)',
              color: '#fff',
              fontWeight: 800,
              fontSize: 15,
              cursor: confirming ? 'wait' : 'pointer',
              boxShadow: confirming ? 'none' : '0 4px 16px rgba(37,99,235,0.28)',
            }}
          >
            {confirming ? '처리 중...' : (requireCheck ? '확인하고 체크하기' : '확인했습니다')}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

function TrainingPendingGateModal({ items, onConfirm }) {
  useEffect(() => {
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, []);

  if (!items?.length) return null;

  return createPortal(
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 10000,
        background: 'rgba(15,23,42,0.62)',
        backdropFilter: 'blur(4px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 20,
      }}
      role="presentation"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="attendance-training-title"
        style={{
          width: '100%',
          maxWidth: 460,
          maxHeight: '85vh',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          background: '#fff',
          borderRadius: 20,
          boxShadow: '0 20px 60px rgba(0,0,0,0.28)',
          animation: 'modalIn 0.22s ease',
        }}
      >
        <div style={{ padding: '22px 22px 14px', borderBottom: '1px solid #f1f5f9' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14 }}>
            <div style={{ flexShrink: 0, lineHeight: 0 }}>
              <DashboardCardIcon cardId="internal-training" />
            </div>
            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={{ fontSize: 11, fontWeight: 800, color: TRAINING_BLUE, marginBottom: 6 }}>
                출퇴근 체크 전 미이수 교육 안내
              </div>
              <h3 id="attendance-training-title" style={{ margin: 0, fontSize: 18, fontWeight: 900, color: '#0f172a', lineHeight: 1.35 }}>
                이수해야 할 사내교육 {items.length}건
              </h3>
              <p style={{ margin: '8px 0 0', fontSize: 13, color: '#64748b', lineHeight: 1.5 }}>
                ERP 메뉴 <strong style={{ color: '#1e293b' }}>사내교육 이수</strong>에서 동영상을 끝까지 시청해 주세요.
              </p>
            </div>
          </div>
        </div>
        <div style={{ padding: '14px 22px', overflowY: 'auto', flex: 1 }}>
          <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 10 }}>
            {items.map((it) => (
              <li
                key={it.seq}
                style={{
                  border: `1px solid ${it.overdue ? '#fecaca' : 'rgba(33,150,243,0.22)'}`,
                  borderRadius: 14,
                  padding: '14px 16px',
                  background: it.overdue ? '#fef2f2' : '#f8fbff',
                }}
              >
                <div style={{ fontWeight: 800, fontSize: 14, color: '#0f172a', lineHeight: 1.4, wordBreak: 'keep-all' }}>
                  {it.title}
                  {it.overdue ? <span style={{ color: '#dc2626', marginLeft: 6, fontSize: 12 }}>기한 초과</span> : null}
                </div>
                {it.description ? (
                  <p style={{
                    margin: '8px 0 0',
                    fontSize: 13,
                    color: '#64748b',
                    lineHeight: 1.55,
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                  }}
                  >
                    {it.description}
                  </p>
                ) : null}
                <div style={{ fontSize: 12, color: '#94a3b8', marginTop: it.description ? 8 : 6 }}>
                  진행 {Math.round(it.progress_percent || 0)}% · 마감 {it.due_date_end || '미정'}
                </div>
              </li>
            ))}
          </ul>
        </div>
        <div style={{ padding: '16px 22px 20px', borderTop: '1px solid #f1f5f9' }}>
          <button
            type="button"
            onClick={onConfirm}
            style={{
              width: '100%',
              padding: '14px 0',
              borderRadius: 14,
              border: 'none',
              background: `linear-gradient(135deg, #1976D2, ${TRAINING_BLUE})`,
              color: '#fff',
              fontWeight: 800,
              fontSize: 15,
              cursor: 'pointer',
              boxShadow: '0 4px 16px rgba(33,150,243,0.32)',
            }}
          >
            확인했습니다 (출퇴근 계속)
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

/* ── 사무실 좌표 ── */
const OFFICE_LAT = 35.199059499985;
const OFFICE_LNG = 126.86376660732;
function calcDistKm(lat1, lng1, lat2, lng2) {
  const R = 6371;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}
function getNextDateStr(dateStr) {
  const d = new Date(dateStr + 'T12:00:00');
  d.setDate(d.getDate() + 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
function calcAllowances(logs, settings) {
  if (!logs || logs.length === 0) return {};
  const result = {};
  const EARLY_MINS   = 6 * 60 + 5;
  const MORNING_MINS = 9 * 60;
  const toMins = (dtStr) => { const d = new Date(dtStr); return d.getHours() * 60 + d.getMinutes(); };
  const addTag = (seq, tag, amount) => {
    if (!result[seq]) result[seq] = [];
    if (!result[seq].some(t => t.tag === tag)) result[seq].push({ tag, amount });
  };
  const byUser = {};
  for (const log of logs) {
    const k = log.user_email || '_';
    if (!byUser[k]) byUser[k] = [];
    byUser[k].push(log);
  }
  for (const userLogs of Object.values(byUser)) {
    userLogs.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
    const earlyByDate = {};
    const overnightByDate = {};
    for (let i = 0; i < userLogs.length; i++) {
      const log = userLogs[i];
      if (log.type !== 'go') continue;
      const mins = toMins(log.created_at);
      if (mins >= EARLY_MINS) continue;
      const dateStr = String(log.created_at).slice(0, 10);
      if (earlyByDate[dateStr]) continue;
      let granted = false;
      if (log.status === 'normal') {
        granted = true;
      } else if (log.status === 'approved') {
        const baseMs = new Date(log.created_at).getTime();
        for (let j = i + 1; j < userLogs.length; j++) {
          const next = userLogs[j];
          if (next.type !== 'go') continue;
          if (new Date(next.created_at).getTime() - baseMs > 60 * 60 * 1000) break;
          if (next.lat != null && next.lng != null) {
            const fromOffice = calcDistKm(OFFICE_LAT, OFFICE_LNG, next.lat, next.lng);
            const fromFirst = (log.lat != null && log.lng != null)
              ? calcDistKm(log.lat, log.lng, next.lat, next.lng)
              : 0;
            if (fromFirst >= 30 || fromOffice >= 30) { granted = true; break; }
          }
        }
        if (!granted) {
          const hasLeave80SameDay = userLogs.some(l =>
            l.type === 'leave' &&
            (l.status === 'approved' || l.status === 'normal') &&
            String(l.created_at).slice(0, 10) === dateStr &&
            l.lat != null && l.lng != null &&
            calcDistKm(OFFICE_LAT, OFFICE_LNG, l.lat, l.lng) >= 80
          );
          if (hasLeave80SameDay) granted = true;
        }
      }
      if (granted) { addTag(log.seq, '새벽수당', settings?.early_morning); earlyByDate[dateStr] = log; }
    }
    for (const [dateStr] of Object.entries(earlyByDate)) {
      const hasLeave80 = userLogs.some(l =>
        l.type === 'leave' && (l.status === 'approved' || l.status === 'normal') &&
        String(l.created_at).slice(0, 10) === dateStr &&
        l.lat != null && l.lng != null && calcDistKm(OFFICE_LAT, OFFICE_LNG, l.lat, l.lng) >= 80
      );
      if (!hasLeave80) continue;
      const nextDate = getNextDateStr(dateStr);
      const nextGo = userLogs.find(l =>
        l.type === 'go' && (l.status === 'approved' || l.status === 'normal') &&
        String(l.created_at).slice(0, 10) === nextDate && toMins(l.created_at) < MORNING_MINS &&
        l.lat != null && l.lng != null &&
        calcDistKm(OFFICE_LAT, OFFICE_LNG, l.lat, l.lng) >= 80
      );
      if (nextGo) { addTag(nextGo.seq, '외박수당', settings?.overnight_1day); overnightByDate[nextDate] = nextGo; }
    }
    const MULTI_NIGHT_MINS = MORNING_MINS;
    let pendingOvernight = { ...overnightByDate };
    let safetyLimit = 0;
    while (Object.keys(pendingOvernight).length > 0 && safetyLimit++ < 30) {
      const nextPending = {};
      for (const [dateStr] of Object.entries(pendingOvernight)) {
        const hasLeave80 = userLogs.some(l =>
          l.type === 'leave' && (l.status === 'approved' || l.status === 'normal') &&
          String(l.created_at).slice(0, 10) === dateStr &&
          l.lat != null && l.lng != null && calcDistKm(OFFICE_LAT, OFFICE_LNG, l.lat, l.lng) >= 80
        );
        if (!hasLeave80) continue;
        const nextDate = getNextDateStr(dateStr);
        if (overnightByDate[nextDate]) continue;
        const nextGo = userLogs.find(l =>
          l.type === 'go' && (l.status === 'approved' || l.status === 'normal') &&
          String(l.created_at).slice(0, 10) === nextDate &&
          toMins(l.created_at) < MULTI_NIGHT_MINS &&
          l.lat != null && l.lng != null && calcDistKm(OFFICE_LAT, OFFICE_LNG, l.lat, l.lng) >= 80
        );
        if (nextGo) {
          addTag(nextGo.seq, '연박수당', settings?.multi_night);
          overnightByDate[nextDate] = nextGo;
          nextPending[nextDate] = nextGo;
        }
      }
      pendingOvernight = nextPending;
    }
  }
  return result;
}

/* ── 날짜 헬퍼 ── */
function getMonthRange(monthsAgo = 0) {
  const now = new Date();
  const d = new Date(now.getFullYear(), now.getMonth() - monthsAgo, 1);
  const firstDay = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
  const lastD = monthsAgo === 0 ? now : new Date(d.getFullYear(), d.getMonth() + 1, 0);
  return {
    from: firstDay,
    to: `${lastD.getFullYear()}-${String(lastD.getMonth() + 1).padStart(2, '0')}-${String(lastD.getDate()).padStart(2, '0')}`,
  };
}

/* ── 빠른 기간 선택 버튼 그룹 ── */
function QuickDateBar({ mode, onMode, dateFrom, dateTo, onFrom, onTo }) {
  const btnStyle = (m) => ({
    height: '30px', padding: '0 10px', border: '1px solid', borderRadius: '8px', fontSize: '12px', fontWeight: '700',
    cursor: 'pointer', whiteSpace: 'nowrap',
    background: mode === m ? '#2563eb' : '#f1f5f9',
    color: mode === m ? '#fff' : '#475569',
    borderColor: mode === m ? '#2563eb' : '#e2e8f0',
  });
  return (
    <div style={{ display: 'flex', gap: '4px', alignItems: 'center', flexWrap: 'wrap' }}>
      {[['this', '이번달'], ['1', '1달전'], ['2', '2달전'], ['3', '3달전'], ['custom', '기간선택']].map(([m, lbl]) => (
        <button key={m} type="button" style={btnStyle(m)} onClick={() => onMode(m)}>{lbl}</button>
      ))}
      {mode === 'custom' && (
        <>
          <input type="date" value={dateFrom} onChange={e => onFrom(e.target.value)}
            style={{ height: '30px', padding: '0 8px', borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '12px', color: '#334155' }} />
          <span style={{ fontSize: '11px', color: '#94a3b8' }}>~</span>
          <input type="date" value={dateTo} onChange={e => onTo(e.target.value)}
            style={{ height: '30px', padding: '0 8px', borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '12px', color: '#334155' }} />
        </>
      )}
    </div>
  );
}

const ATT_TYPE_META = {
  go: { label: '출근', bg: '#d1fae5', color: '#065f46', dot: '#059669', accent: '#16a34a' },
  leave: { label: '퇴근', bg: '#fce7f3', color: '#9d174d', dot: '#be185d', accent: '#2563eb' },
  trip_start: { label: '출장출발', bg: '#fef3c7', color: '#92400e', dot: '#d97706', accent: '#d97706' },
  trip_end: { label: '출장복귀', bg: '#ede9fe', color: '#5b21b6', dot: '#7c3aed', accent: '#7c3aed' },
};
const attTypeMeta = (type) => ATT_TYPE_META[type] || ATT_TYPE_META.leave;
const isTripType = (type) => type === 'trip_start' || type === 'trip_end';

// ─── GPS 위치 확인 모달 (출근/퇴근 전 현재위치 확인) ─────────────────────────
function LocationConfirmModal({ type, lat, lng, accuracy, onConfirm, onCancel }) {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const [mapStatus, setMapStatus] = useState('loading');

  const typeLabel = attTypeMeta(type).label;
  const typeColor = attTypeMeta(type).accent;
  const naverUrl = `https://map.naver.com/v5/?c=${lng},${lat},16,0,0,0,dh`;

  useEffect(() => {
    if (!lat || !lng) return;
    let cancelled = false;
    let pollTimer = null;

    function initMap() {
      if (cancelled || !mapRef.current || mapInstanceRef.current) return;
      try {
        const center = new window.naver.maps.LatLng(Number(lat), Number(lng));
        const map = new window.naver.maps.Map(mapRef.current, {
          center, zoom: 17, mapTypeId: window.naver.maps.MapTypeId.NORMAL,
        });
        mapInstanceRef.current = map;
        new window.naver.maps.Marker({ position: center, map });
        if (!cancelled) setMapStatus('ok');
      } catch { if (!cancelled) setMapStatus('error'); }
    }

    function tryInit() {
      if (cancelled) return false;
      if (window.naver?.maps?.Map) {
        requestAnimationFrame(() => setTimeout(() => initMap(), 50));
        return true;
      }
      return false;
    }

    if (tryInit()) return () => { cancelled = true; };

    const ncpClientId =
      window.__RUNTIME_CONFIG__?.REACT_APP_NAVER_MAPS_CLIENT_ID ||
      window.__ENV__?.REACT_APP_NAVER_MAPS_CLIENT_ID ||
      process.env.REACT_APP_NAVER_MAPS_CLIENT_ID || '';

    if (!ncpClientId) { setMapStatus('error'); return () => { cancelled = true; }; }

    if (document.querySelector('script[src*="openapi.map.naver.com"]')) {
      pollTimer = setInterval(() => { if (tryInit()) clearInterval(pollTimer); }, 150);
      return () => { cancelled = true; clearInterval(pollTimer); };
    }

    const script = document.createElement('script');
    script.src = `https://openapi.map.naver.com/openapi/v3/maps.js?ncpKeyId=${encodeURIComponent(ncpClientId)}`;
    script.async = true;
    script.onload = () => tryInit();
    script.onerror = () => { if (!cancelled) setMapStatus('error'); };
    document.head.appendChild(script);
    pollTimer = setInterval(() => { if (tryInit()) clearInterval(pollTimer); }, 150);

    return () => { cancelled = true; clearInterval(pollTimer); };
  }, [lat, lng]);

  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 9999,
        background: 'rgba(15,23,42,0.65)', backdropFilter: 'blur(5px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px',
      }}
    >
      <div style={{
        width: '100%', maxWidth: '420px', background: '#fff',
        borderRadius: '20px', boxShadow: '0 24px 64px rgba(15,23,42,0.28)',
        animation: 'modalIn 0.22s ease', overflow: 'hidden',
      }}>
        {/* 헤더 */}
        <div style={{
          padding: '14px 18px',
          background: `linear-gradient(135deg, ${typeColor}, ${typeColor}cc)`,
          color: '#fff',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <div>
            <div style={{ fontWeight: '900', fontSize: '15px' }}>📍 현재 위치 확인</div>
            <div style={{ fontSize: '12px', opacity: 0.88, marginTop: '2px' }}>
              {typeLabel} 체크 전 위치를 확인해 주세요
            </div>
          </div>
          <a href={naverUrl} target="_blank" rel="noopener noreferrer"
            style={{
              padding: '5px 12px', borderRadius: '8px',
              background: 'rgba(255,255,255,0.22)', color: '#fff',
              fontSize: '11px', fontWeight: '700', textDecoration: 'none',
              border: '1px solid rgba(255,255,255,0.4)',
            }}
          >네이버지도 ↗</a>
        </div>

        {/* 지도 */}
        <div style={{ position: 'relative', width: '100%', height: '300px', background: '#e8f0fe' }}>
          <div ref={mapRef} style={{ position: 'absolute', inset: 0 }} />
          {mapStatus === 'loading' && (
            <div style={{
              position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column',
              alignItems: 'center', justifyContent: 'center', background: '#f0f9ff', gap: '10px',
            }}>
              <div style={{
                width: '34px', height: '34px', borderRadius: '50%',
                border: '3px solid #bae6fd', borderTopColor: typeColor,
                animation: 'spin 0.8s linear infinite',
              }} />
              <div style={{ fontSize: '12px', color: '#64748b', fontWeight: '600' }}>지도 불러오는 중...</div>
            </div>
          )}
          {mapStatus === 'error' && (
            <div style={{
              position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column',
              alignItems: 'center', justifyContent: 'center', background: '#f8fafc', gap: '10px',
            }}>
              <div style={{ fontSize: '28px' }}>🗺️</div>
              <div style={{ fontSize: '12px', color: '#64748b', fontWeight: '600' }}>지도를 불러올 수 없습니다.</div>
              <a href={naverUrl} target="_blank" rel="noopener noreferrer"
                style={{
                  padding: '7px 16px', borderRadius: '8px', background: typeColor,
                  color: '#fff', fontSize: '12px', fontWeight: '700', textDecoration: 'none',
                }}
              >네이버지도에서 보기 ↗</a>
            </div>
          )}
        </div>

        {/* 좌표/정확도 */}
        <div style={{
          padding: '8px 16px', background: '#f8fafc', borderTop: '1px solid #e2e8f0',
          fontSize: '11px', color: '#64748b', fontFamily: 'monospace',
          display: 'flex', justifyContent: 'space-between',
        }}>
          <span>위도 {Number(lat).toFixed(6)} / 경도 {Number(lng).toFixed(6)}</span>
          {accuracy != null && <span>정확도 ±{Math.round(accuracy)}m</span>}
        </div>

        {/* 질문 + 버튼 */}
        <div style={{ padding: '16px 18px 18px' }}>
          <div style={{
            textAlign: 'center', fontWeight: '800', fontSize: '16px',
            color: '#0f172a', marginBottom: '14px',
          }}>
            이 위치가 현재 위치가 맞습니까?
          </div>
          <div style={{ display: 'flex', gap: '10px' }}>
            <button type="button" onClick={onCancel}
              style={{
                flex: 1, padding: '13px 0', borderRadius: '12px',
                border: '1.5px solid #e2e8f0', background: '#f8fafc',
                fontWeight: '700', fontSize: '14px', color: '#64748b', cursor: 'pointer',
              }}
            >취소</button>
            <button type="button" onClick={onConfirm}
              style={{
                flex: 2, padding: '13px 0', borderRadius: '12px',
                border: 'none',
                background: `linear-gradient(135deg, ${typeColor}, ${typeColor}bb)`,
                fontWeight: '900', fontSize: '14px', color: '#fff', cursor: 'pointer',
                boxShadow: `0 4px 14px ${typeColor}44`,
              }}
            >✅ 맞습니다 — {typeLabel} 체크</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── 네이버 지도 미니맵 모달 ────────────────────────────────────────────────
function NaverMapModal({ lat, lng, label, onClose }) {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const [mapStatus, setMapStatus] = useState('loading'); // 'loading' | 'ok' | 'error'

  const naverUrl = `https://map.naver.com/v5/?c=${lng},${lat},16,0,0,0,dh`;

  useEffect(() => {
    if (!lat || !lng) return;
    let cancelled = false;
    let pollTimer = null;

    function initMap() {
      if (cancelled) return;
      if (!mapRef.current) return;
      if (mapInstanceRef.current) return;
      try {
        const center = new window.naver.maps.LatLng(Number(lat), Number(lng));
        const map = new window.naver.maps.Map(mapRef.current, {
          center,
          zoom: 16,
          mapTypeId: window.naver.maps.MapTypeId.NORMAL,
        });
        mapInstanceRef.current = map;
        new window.naver.maps.Marker({ position: center, map });
        if (!cancelled) setMapStatus('ok');
      } catch (e) {
        if (!cancelled) setMapStatus('error');
      }
    }

    function tryInit() {
      if (cancelled) return;
      if (window.naver?.maps?.Map) {
        // DOM이 충분히 그려지도록 한 프레임 뒤에 실행
        requestAnimationFrame(() => {
          setTimeout(() => initMap(), 50);
        });
        return true;
      }
      return false;
    }

    function startPolling() {
      pollTimer = setInterval(() => {
        if (tryInit()) clearInterval(pollTimer);
      }, 150);
    }

    // SDK 이미 준비됐으면 즉시
    if (tryInit()) return () => { cancelled = true; };

    // SDK 키 확인 (Schedule.js 와 동일한 순서)
    const ncpClientId =
      (window.__RUNTIME_CONFIG__?.REACT_APP_NAVER_MAPS_CLIENT_ID) ||
      (window.__ENV__?.REACT_APP_NAVER_MAPS_CLIENT_ID) ||
      process.env.REACT_APP_NAVER_MAPS_CLIENT_ID ||
      (typeof localStorage !== 'undefined' ? localStorage.getItem('NAVER_MAPS_CLIENT_ID') : '') ||
      '';

    if (!ncpClientId) {
      setMapStatus('error');
      return () => { cancelled = true; };
    }

    // script 이미 삽입됐으면 폴링만
    if (document.querySelector('script[src*="openapi.map.naver.com"]')) {
      startPolling();
      return () => { cancelled = true; clearInterval(pollTimer); };
    }

    // script 새로 로드
    const script = document.createElement('script');
    script.src = `https://openapi.map.naver.com/openapi/v3/maps.js?ncpKeyId=${encodeURIComponent(ncpClientId)}`;
    script.async = true;
    script.onload = () => tryInit();
    script.onerror = () => { if (!cancelled) setMapStatus('error'); };
    document.head.appendChild(script);
    startPolling();

    return () => {
      cancelled = true;
      clearInterval(pollTimer);
    };
  }, [lat, lng]);

  if (!lat || !lng) return null;

  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 9999,
        background: 'rgba(15,23,42,0.6)', backdropFilter: 'blur(4px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '16px',
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={{
        width: '100%', maxWidth: 'min(1040px, 96vw)',
        background: '#fff', borderRadius: '16px',
        boxShadow: '0 20px 60px rgba(15,23,42,0.25)',
      }}>
        {/* 헤더 */}
        <div style={{
          padding: '14px 18px', borderRadius: '16px 16px 0 0',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          background: 'linear-gradient(135deg,#0ea5e9,#38bdf8)', color: '#fff',
        }}>
          <div>
            <div style={{ fontWeight: '800', fontSize: '15px' }}>📍 체크 위치</div>
            {label && <div style={{ fontSize: '11px', opacity: 0.88, marginTop: '2px' }}>{label}</div>}
          </div>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <a
              href={naverUrl} target="_blank" rel="noopener noreferrer"
              style={{
                padding: '5px 12px', borderRadius: '8px',
                background: 'rgba(255,255,255,0.25)', color: '#fff',
                fontSize: '12px', fontWeight: '700', textDecoration: 'none',
                border: '1px solid rgba(255,255,255,0.4)',
              }}
            >
              네이버지도 열기 ↗
            </a>
            <button onClick={onClose} style={{
              width: '28px', height: '28px', borderRadius: '8px',
              border: '1px solid rgba(255,255,255,0.4)', background: 'rgba(255,255,255,0.2)',
              color: '#fff', cursor: 'pointer', fontSize: '16px', lineHeight: 1,
            }}>✕</button>
          </div>
        </div>

        {/* 지도 컨테이너 — overflow 제거, position:relative 필수 */}
        <div style={{ position: 'relative', width: '100%', height: 'min(680px, 65vh)', background: '#e8f0fe' }}>
          <div
            ref={mapRef}
            style={{ position: 'absolute', inset: 0 }}
          />
          {/* 로딩 오버레이 */}
          {mapStatus === 'loading' && (
            <div style={{
              position: 'absolute', inset: 0, display: 'flex',
              flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
              background: '#f0f9ff', gap: '12px',
            }}>
              <div style={{
                width: '36px', height: '36px', borderRadius: '50%',
                border: '3px solid #bae6fd', borderTopColor: '#0ea5e9',
                animation: 'spin 0.8s linear infinite',
              }} />
              <div style={{ fontSize: '13px', color: '#64748b', fontWeight: '600' }}>지도 불러오는 중...</div>
            </div>
          )}
          {/* 에러 오버레이 */}
          {mapStatus === 'error' && (
            <div style={{
              position: 'absolute', inset: 0, display: 'flex',
              flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
              background: '#f8fafc', gap: '12px',
            }}>
              <div style={{ fontSize: '32px' }}>🗺️</div>
              <div style={{ fontSize: '13px', color: '#64748b', fontWeight: '600' }}>지도를 표시할 수 없습니다.</div>
              <a
                href={naverUrl} target="_blank" rel="noopener noreferrer"
                style={{
                  padding: '8px 20px', borderRadius: '10px',
                  background: '#0ea5e9', color: '#fff',
                  fontSize: '13px', fontWeight: '700', textDecoration: 'none',
                }}
              >
                네이버지도에서 보기 ↗
              </a>
            </div>
          )}
        </div>

        {/* 좌표 */}
        <div style={{
          padding: '10px 18px', borderRadius: '0 0 16px 16px',
          borderTop: '1px solid #e2e8f0',
          fontSize: '12px', color: '#64748b', fontFamily: 'monospace',
          background: '#f8fafc',
        }}>
          위도 {Number(lat).toFixed(6)} / 경도 {Number(lng).toFixed(6)}
        </div>
      </div>

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

const PAGE_SIZE = 50;

const thS = {
  padding: '10px 14px',
  textAlign: 'left',
  fontWeight: '700',
  fontSize: '12px',
  color: '#64748b',
  borderBottom: '1px solid #e2e8f0',
  whiteSpace: 'nowrap',
  background: '#f8fafc',
};
const tdS = { padding: '10px 14px', color: '#334155', fontSize: '13px' };
const pageBtnS = (disabled) => ({
  padding: '6px 12px',
  border: '1px solid #e2e8f0',
  borderRadius: '6px',
  background: disabled ? '#f8fafc' : '#fff',
  color: disabled ? '#cbd5e1' : '#334155',
  cursor: disabled ? 'not-allowed' : 'pointer',
  fontSize: '13px',
});

export default function AttendancePage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const apiBase = getApiBaseUrl();

  const [todayStatus, setTodayStatus] = useState({ last_go: null, last_leave: null, last_trip_start: null, last_trip_end: null });
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(false);
  const [checkLoading, setCheckLoading] = useState(false);
  const [msg, setMsg] = useState(null);
  const [tripManual, setTripManual] = useState(null); // { type, time: 'YYYY-MM-DDTHH:MM' }
  const [tripManualSaving, setTripManualSaving] = useState(false);
  const { from: initFrom, to: initTo } = getMonthRange(0);
  const [dateFrom, setDateFrom] = useState(initFrom);
  const [dateTo, setDateTo] = useState(initTo);
  const [dateMode, setDateMode] = useState('this');
  const [page, setPage] = useState(0);
  const [mapTarget, setMapTarget] = useState(null); // { lat, lng, label }
  const [gpsStatus, setGpsStatus] = useState('checking'); // 'checking' | 'ok' | 'denied' | 'unavailable'
  // GPS 위치 확인 모달: { type, lat, lng, accuracy }
  const [locationConfirmModal, setLocationConfirmModal] = useState(null);
  // 100m 이탈 확인 모달: { type, lat, lng, accuracy, dist }
  const [outOfRangeModal, setOutOfRangeModal] = useState(null);
  const [latestNotice, setLatestNotice] = useState(null);
  const [noticeGate, setNoticeGate] = useState({ open: false, pendingType: null });
  const [noticeConfirming, setNoticeConfirming] = useState(false);
  const [pendingTrainings, setPendingTrainings] = useState([]);
  const [trainingGate, setTrainingGate] = useState({ open: false, pendingType: null });
  // ERP 쓰기 권한 (출퇴근 승인 가능)
  const canWrite = (user?.permissions || []).some(p => ['write', 'admin'].includes(p));

  // 수당 설정 (전체 근태 관리에서 저장한 값)
  const [allowanceSettings, setAllowanceSettings] = useState(null);

  const getAuthHeaders = useCallback(() => {
    const token = user?.token ? String(user.token) : '';
    return token ? { Authorization: `Bearer ${token}` } : {};
  }, [user]);

  const apiFetch = useCallback(async (path, opts = {}) => {
    const res = await fetch(`${apiBase}${path}`, {
      ...opts,
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders(),
        ...(opts.headers || {}),
      },
      credentials: 'include',
    });
    const text = await res.text();
    const json = text ? JSON.parse(text) : null;
    if (!res.ok) {
      const detail = json?.detail || json?.message || `${res.status}`;
      throw new Error(String(detail));
    }
    return json;
  }, [apiBase, getAuthHeaders]);

  const fetchTodayStatus = useCallback(async () => {
    try {
      const data = await apiFetch('/api/erp_attendance/today_status');
      if (data?.ok) setTodayStatus(data);
    } catch (_) {}
  }, [apiFetch]);

  const fetchLogs = useCallback(async (pg = 0) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: PAGE_SIZE, offset: pg * PAGE_SIZE });
      if (dateFrom) params.set('date_from', dateFrom);
      if (dateTo) params.set('date_to', dateTo);
      // 개인 페이지: 항상 본인 이메일 명시 (관리자도 본인 것만 보이도록)
      if (user?.email) params.set('email', user.email);
      const data = await apiFetch(`/api/erp_attendance/logs?${params}`);
      if (data?.ok) {
        setLogs(data.logs || []);
        setTotal(data.total || 0);
        setIsAdmin(!!data.is_admin);
      }
    } catch (e) {
      setMsg({ type: 'error', text: '로그 조회 실패: ' + e.message });
    } finally {
      setLoading(false);
    }
  }, [apiFetch, dateFrom, dateTo, user]);

  const fetchLatestNotice = useCallback(async () => {
    try {
      const q = new URLSearchParams({ push_type: 'broadcast', limit: '1', offset: '0' });
      const [historyData, mineData] = await Promise.all([
        apiFetch(`/api/erp/push/history?${q}`),
        apiFetch('/api/erp/push/my-broadcasts?limit=10'),
      ]);
      const items = (historyData.items || []).filter(
        (h) => (h.pushSubType || 'broadcast') !== 'individual',
      );
      const latest = items[0];
      if (!latest) {
        setLatestNotice(null);
        setNoticeGate({ open: false, pendingType: null });
        return;
      }
      const readMap = {};
      (mineData.items || []).forEach((n) => {
        if (n.isRead) readMap[n.seq] = true;
      });
      setLatestNotice({
        seq: latest.seq,
        title: latest.title || 'MRT 공지',
        body: latest.body || '',
        read: !!readMap[latest.seq],
        receivedAt: latest.createdAt,
      });
    } catch (_) {
      setLatestNotice(null);
      setNoticeGate({ open: false, pendingType: null });
    }
  }, [apiFetch]);

  useEffect(() => {
    if (!latestNotice?.seq) return;
    setNoticeGate({ open: true, pendingType: null });
  }, [latestNotice?.seq]);

  const fetchPendingTrainings = useCallback(async () => {
    try {
      const data = await apiFetch('/api/erp_internal_training/pending');
      setPendingTrainings(Array.isArray(data?.items) ? data.items : []);
    } catch (_) {
      setPendingTrainings([]);
    }
  }, [apiFetch]);

  useEffect(() => {
    fetchTodayStatus();
    fetchLogs(0);
    fetchLatestNotice();
    fetchPendingTrainings();
  }, [fetchTodayStatus, fetchLogs, fetchLatestNotice, fetchPendingTrainings]);

  // 수당 설정 조회
  useEffect(() => {
    apiFetch('/api/erp_attendance/allowance_settings')
      .then(d => { if (d?.ok) setAllowanceSettings(d.settings || null); })
      .catch(() => {});
  }, [apiFetch]);

  // 수당 계산
  const allowances = useMemo(() => calcAllowances(logs, allowanceSettings), [logs, allowanceSettings]);

  // (기능 권한은 이제 ERP write 권한으로 통합 — 별도 조회 불필요)

  // GPS 사용 가능 여부 사전 체크
  useEffect(() => {
    const isFlutter = !!window.FlutterNativeGPS;
    if (isFlutter) {
      setGpsStatus('ok');
      return;
    }
    if (!navigator.geolocation) {
      setGpsStatus('unavailable');
      return;
    }
    navigator.permissions.query({ name: 'geolocation' }).then((result) => {
      if (result.state === 'denied') {
        setGpsStatus('denied');
      } else {
        setGpsStatus('ok');
      }
      result.onchange = () => {
        setGpsStatus(result.state === 'denied' ? 'denied' : 'ok');
      };
    }).catch(() => {
      setGpsStatus('ok'); // permissions API 미지원 시 일단 허용
    });
  }, []);

  // 회사 위치: 광주 광역시 북구 첨단연신로 12 (연제동) - (주)엠알티
  const OFFICE_LAT = 35.199059499985;
  const OFFICE_LNG = 126.86376660732;
  const OFFICE_RADIUS_M = 100;

  function calcDistance(lat1, lng1, lat2, lng2) {
    const R = 6371000; // 지구 반지름(m)
    const toRad = (d) => (d * Math.PI) / 180;
    const dLat = toRad(lat2 - lat1);
    const dLng = toRad(lng2 - lng1);
    const a =
      Math.sin(dLat / 2) ** 2 +
      Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  }

  function getGpsLocation(timeoutMs = 10000) {
    return new Promise((resolve, reject) => {
      const isFlutter = !!window.FlutterNativeGPS;
      if (isFlutter) {
        const cbName = '__gps_cb_' + Date.now();
        const timer = setTimeout(() => {
          delete window[cbName];
          reject(new Error('Flutter GPS 타임아웃'));
        }, timeoutMs);
        window[cbName] = (data) => {
          clearTimeout(timer);
          delete window[cbName];
          try {
            const parsed = typeof data === 'string' ? JSON.parse(data) : data;
            if (parsed.error) return reject(new Error(parsed.error));
            resolve({ lat: parsed.lat, lng: parsed.lng, accuracy: parsed.accuracy });
          } catch {
            reject(new Error('GPS 데이터 파싱 오류'));
          }
        };
        window.FlutterNativeGPS.postMessage(
          JSON.stringify({ action: 'GET_LOCATION', callback: cbName })
        );
      } else if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (pos) => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude, accuracy: pos.coords.accuracy }),
          (err) => {
            const msgMap = {
              1: '위치 권한이 거부되었습니다. 브라우저 설정에서 허용해 주세요.',
              2: 'GPS 신호를 찾을 수 없습니다. 잠시 후 다시 시도해 주세요.',
              3: 'GPS 응답 시간이 초과되었습니다.',
            };
            reject(new Error(msgMap[err.code] || ('GPS 오류: ' + err.message)));
          },
          { enableHighAccuracy: true, timeout: timeoutMs, maximumAge: 0 }
        );
      } else {
        reject(new Error('이 기기는 GPS를 지원하지 않습니다.'));
      }
    });
  }

  async function submitCheck(type, lat, lng, accuracy, status = 'normal') {
    try {
      const deviceInfo = navigator.userAgent.slice(0, 150);
      const data = await apiFetch('/api/erp_attendance/check', {
        method: 'POST',
        body: JSON.stringify({ type, lat, lng, accuracy, device_info: deviceInfo, status }),
      });
      if (data?.ok) {
        const label = attTypeMeta(type).label;
        const timeStr = data.created_at ? String(data.created_at).slice(11, 16) : '';
        const extra = status === 'pending' ? ' (확인필요 — 부서장 승인 대기)' : '';
        setMsg({ type: status === 'pending' ? 'warn' : 'success', text: `${label} 체크 완료! (${timeStr})${extra}` });
        fetchTodayStatus();
        fetchLogs(0);
        setPage(0);
      }
    } catch (e) {
      setMsg({ type: 'error', text: '체크 실패: ' + e.message });
    } finally {
      setCheckLoading(false);
    }
  }

  async function startGpsCheck(type) {
    setCheckLoading(true);
    setMsg(null);
    let lat = null, lng = null, accuracy = null;
    try {
      const loc = await getGpsLocation(15000);
      lat = loc.lat; lng = loc.lng; accuracy = loc.accuracy;
    } catch (e) {
      setMsg({ type: 'error', text: 'GPS 필요: ' + e.message });
      setGpsStatus(e.message.includes('권한') ? 'denied' : 'ok');
      setCheckLoading(false);
      return;
    }
    setCheckLoading(false);
    setLocationConfirmModal({ type, lat, lng, accuracy });
  }

  async function confirmLatestNotice() {
    if (!latestNotice?.seq) return;
    setNoticeConfirming(true);
    try {
      await apiFetch('/api/erp/push/read', {
        method: 'POST',
        body: JSON.stringify({ push_seq: latestNotice.seq }),
      });
      setLatestNotice((prev) => (prev ? { ...prev, read: true } : null));
      const pendingType = noticeGate.pendingType;
      setNoticeGate({ open: false, pendingType: null });
      if (pendingType) await startGpsCheck(pendingType);
    } catch (e) {
      setMsg({ type: 'error', text: '공지 확인 처리 실패: ' + e.message });
    } finally {
      setNoticeConfirming(false);
    }
  }

  function openLatestNoticeModal(pendingType = null) {
    if (!latestNotice?.seq) return;
    setNoticeGate({ open: true, pendingType });
  }

  function proceedAfterTrainingGate() {
    const type = trainingGate.pendingType;
    setTrainingGate({ open: false, pendingType: null });
    if (latestNotice?.seq) {
      openLatestNoticeModal(type);
      return;
    }
    startGpsCheck(type);
  }

  async function handleCheck(type) {
    if (pendingTrainings.length > 0) {
      setTrainingGate({ open: true, pendingType: type });
      return;
    }
    if (latestNotice?.seq) {
      openLatestNoticeModal(type);
      return;
    }
    await startGpsCheck(type);
  }

  async function handleProceedCheck({ type, lat, lng, accuracy }) {
    setLocationConfirmModal(null);
    setCheckLoading(true);
    // 회사 위치에서 100m 초과 여부 확인 (당일출장 출발·복귀는 현장 기록이라 제외)
    const dist = Math.round(calcDistance(OFFICE_LAT, OFFICE_LNG, lat, lng));
    if (!isTripType(type) && dist > OFFICE_RADIUS_M) {
      setCheckLoading(false);
      setOutOfRangeModal({ type, lat, lng, accuracy, dist });
      return;
    }
    await submitCheck(type, lat, lng, accuracy, 'normal');
  }



  function nowLocalInput() {
    const d = new Date();
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  }

  async function submitTripManual() {
    if (!tripManual?.time) return;
    setTripManualSaving(true);
    try {
      const data = await apiFetch('/api/erp_attendance/manual', {
        method: 'POST',
        body: JSON.stringify({ type: tripManual.type, checkin_time: tripManual.time.replace('T', ' ') }),
      });
      if (data?.ok) {
        const label = attTypeMeta(tripManual.type).label;
        const when = String(data.created_at || '').slice(5, 16).replace('-', '/');
        const pending = data.status === 'pending';
        setMsg({
          type: pending ? 'warn' : 'success',
          text: `${label} 직접 입력 완료 (${when})${pending ? ' — 부서장 승인 후 반영됩니다' : ''}`,
        });
        setTripManual(null);
        fetchTodayStatus();
        fetchLogs(0);
        setPage(0);
      }
    } catch (e) {
      setMsg({ type: 'error', text: '직접 입력 실패: ' + e.message });
    } finally {
      setTripManualSaving(false);
    }
  }

  function handleSearch(e) {
    e?.preventDefault();
    setPage(0);
    fetchLogs(0);
  }

  function handlePageChange(newPage) {
    setPage(newPage);
    fetchLogs(newPage);
  }

  const fmtTime = (ts) => (ts ? String(ts).slice(11, 16) : '--:--');
  const fmtDatetime = (ts) => {
    if (!ts) return '-';
    const s = String(ts).slice(0, 16).replace('T', ' ');
    return s.replace(/^(\d{2})(\d{2})/, '$2');
  };

  const totalPages = Math.ceil(total / PAGE_SIZE);

  const now = new Date();
  const todayStr = `${now.getFullYear()}년 ${now.getMonth()+1}월 ${now.getDate()}일`;
  const weekDay = ['일','월','화','수','목','금','토'][now.getDay()];

  return (
    <div style={{ minHeight: '100vh', background: '#f0f4f8' }}>
      <Header leftActions={
        <button
          type="button"
          onClick={() => navigate('/')}
          style={{
            height: '34px', padding: '0 12px',
            background: 'rgba(255,255,255,0.15)',
            color: 'white',
            border: '1px solid rgba(255,255,255,0.40)',
            borderRadius: '10px',
            cursor: 'pointer',
            fontWeight: 900,
            fontSize: '13px',
            whiteSpace: 'nowrap',
          }}
        >
          ☰ 전체
        </button>
      } />
      <style>{`
        @keyframes fadeUp { from { opacity:0; transform:translateY(10px);} to { opacity:1; transform:translateY(0);} }
        @keyframes spin { from { transform:rotate(0deg);} to { transform:rotate(360deg);} }
        @keyframes modalIn { from { opacity:0; transform:scale(0.93) translateY(12px);} to { opacity:1; transform:scale(1) translateY(0);} }
        .att-check-btn { transition: transform 0.12s, box-shadow 0.12s; }
        .att-check-btn:active { transform: scale(0.96) !important; }
        .att-pin-btn:hover { background: #e0f2fe !important; }
        .att-row:hover { background: #f8fafc !important; }

        @media (max-width: 640px) {
          .att-table thead { display: none; }
          .att-table, .att-table tbody { display: block; }
          .att-row {
            display: grid;
            grid-template-columns: auto 1fr auto auto auto;
            grid-template-rows: auto;
            align-items: center;
            column-gap: 6px;
            padding: 8px 12px;
            border-top: 1px solid #f1f5f9;
          }
          .att-row td { display: block; padding: 0 !important; border: none; white-space: nowrap; overflow: hidden; }

          .att-td-name   { display: none !important; }
          .att-td-type   { grid-column: 1; grid-row: 1; }
          .att-td-status { grid-column: 2; grid-row: 1; }
          .att-td-status > div { flex-wrap: nowrap !important; justify-content: flex-start; }
          .att-td-dt     { grid-column: 3; grid-row: 1; font-size: 11px !important; color: #64748b; }
          .att-td-pin    { grid-column: 4; grid-row: 1; }
          .att-td-del    { grid-column: 5; grid-row: 1; }
        }
      `}</style>

      {trainingGate.open && pendingTrainings.length > 0 ? (
        <TrainingPendingGateModal
          items={pendingTrainings}
          onConfirm={proceedAfterTrainingGate}
        />
      ) : null}

      {noticeGate.open && latestNotice ? (
        <AttendanceNoticeGateModal
          notice={latestNotice}
          confirming={noticeConfirming}
          requireCheck={!!noticeGate.pendingType}
          onConfirm={confirmLatestNotice}
        />
      ) : null}

      {/* ── GPS 현재 위치 확인 모달 ── */}
      {locationConfirmModal && (
        <LocationConfirmModal
          type={locationConfirmModal.type}
          lat={locationConfirmModal.lat}
          lng={locationConfirmModal.lng}
          accuracy={locationConfirmModal.accuracy}
          onConfirm={() => handleProceedCheck(locationConfirmModal)}
          onCancel={() => { setLocationConfirmModal(null); setCheckLoading(false); }}
        />
      )}

      {/* ── 당일출장 출발/복귀 직접 입력 ── */}
      {tripManual && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 9999,
          background: 'rgba(15,23,42,0.55)', backdropFilter: 'blur(4px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px',
        }}>
          <div style={{
            background: '#fff', borderRadius: '20px', width: '100%', maxWidth: '360px',
            padding: '22px 20px 18px', boxShadow: '0 20px 60px rgba(0,0,0,0.25)', animation: 'modalIn 0.22s ease',
          }}>
            <div style={{ fontWeight: '800', fontSize: '17px', color: '#0f172a', marginBottom: '4px' }}>
              🚗 당일출장 시간 직접 입력
            </div>
            <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '16px', lineHeight: 1.5 }}>
              버튼을 못 누른 경우 실제 출발·복귀 시각을 입력하세요.
              {!canWrite && <><br /><b style={{ color: '#b45309' }}>확인필요</b> 상태로 저장되며 부서장 승인 후 반영됩니다.</>}
            </div>
            <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
              {['trip_start', 'trip_end'].map((t) => {
                const m = attTypeMeta(t);
                const on = tripManual.type === t;
                return (
                  <button key={t} type="button"
                    onClick={() => setTripManual((prev) => ({ ...prev, type: t }))}
                    style={{
                      flex: 1, padding: '10px 0', borderRadius: '10px',
                      border: on ? `2px solid ${m.accent}` : '2px solid #e2e8f0',
                      background: on ? m.bg : '#f8fafc', color: on ? m.color : '#94a3b8',
                      fontWeight: '800', fontSize: '14px', cursor: 'pointer',
                    }}
                  >
                    {m.label}
                  </button>
                );
              })}
            </div>
            <input type="datetime-local"
              value={tripManual.time}
              max={nowLocalInput()}
              onChange={(e) => setTripManual((prev) => ({ ...prev, time: e.target.value }))}
              style={{
                width: '100%', boxSizing: 'border-box', height: '44px', padding: '0 12px',
                borderRadius: '10px', border: '1.5px solid #e2e8f0', fontSize: '15px', color: '#0f172a', marginBottom: '16px',
              }}
            />
            <div style={{ display: 'flex', gap: '10px' }}>
              <button type="button" onClick={() => setTripManual(null)} disabled={tripManualSaving}
                style={{
                  flex: 1, padding: '12px 0', borderRadius: '12px', border: '1.5px solid #e2e8f0',
                  background: '#f8fafc', fontWeight: '700', fontSize: '14px', color: '#64748b', cursor: 'pointer',
                }}
              >취소</button>
              <button type="button" onClick={submitTripManual} disabled={tripManualSaving || !tripManual.time}
                style={{
                  flex: 1, padding: '12px 0', borderRadius: '12px', border: 'none',
                  background: `linear-gradient(135deg, ${attTypeMeta(tripManual.type).accent}, ${attTypeMeta(tripManual.type).dot})`,
                  fontWeight: '800', fontSize: '14px', color: '#fff', cursor: 'pointer',
                  opacity: tripManualSaving ? 0.6 : 1,
                }}
              >{tripManualSaving ? '저장 중...' : '저장'}</button>
            </div>
          </div>
        </div>
      )}

      {/* ── 100m 이탈 확인 모달 ── */}
      {outOfRangeModal && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 9999,
          background: 'rgba(15,23,42,0.55)', backdropFilter: 'blur(4px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: '20px',
        }}>
          <div style={{
            background: '#fff', borderRadius: '24px', width: '100%', maxWidth: '360px',
            padding: '28px 24px 20px',
            boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
            animation: 'modalIn 0.22s ease',
          }}>
            {/* 아이콘 */}
            <div style={{ textAlign: 'center', marginBottom: '16px' }}>
              <span style={{
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                width: '56px', height: '56px', borderRadius: '50%',
                background: '#fef3c7', fontSize: '28px',
              }}>⚠️</span>
            </div>
            <div style={{ textAlign: 'center', fontWeight: '800', fontSize: '17px', color: '#0f172a', marginBottom: '10px' }}>
              근무지 외 위치 감지
            </div>
            <div style={{ textAlign: 'center', fontSize: '14px', color: '#475569', lineHeight: '1.6', marginBottom: '20px' }}>
              현재 위치가 근무지에서&nbsp;
              <strong style={{ color: '#b45309' }}>
                {outOfRangeModal.dist >= 1000
                  ? `${(outOfRangeModal.dist / 1000).toFixed(1)}km`
                  : `${outOfRangeModal.dist}m`}
              </strong>
              &nbsp;떨어져 있습니다.<br />
              (허용 범위: 100m 이내)<br /><br />
              현재 위치가 맞습니까?<br />
              <span style={{ fontSize: '12px', color: '#94a3b8' }}>확인 시 <b>확인필요</b> 상태로 저장되며<br />부서장 승인이 필요합니다.</span>
            </div>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button type="button"
                onClick={() => setOutOfRangeModal(null)}
                style={{
                  flex: 1, padding: '13px 0', borderRadius: '14px',
                  border: '1.5px solid #e2e8f0', background: '#f8fafc',
                  fontWeight: '700', fontSize: '14px', color: '#64748b',
                  cursor: 'pointer',
                }}
              >취소</button>
              <button type="button"
                onClick={async () => {
                  const { type, lat, lng, accuracy } = outOfRangeModal;
                  setOutOfRangeModal(null);
                  setCheckLoading(true);
                  await submitCheck(type, lat, lng, accuracy, 'pending');
                }}
                style={{
                  flex: 1, padding: '13px 0', borderRadius: '14px',
                  border: 'none',
                  background: 'linear-gradient(135deg,#92400e,#d97706)',
                  fontWeight: '800', fontSize: '14px', color: '#fff',
                  cursor: 'pointer',
                  boxShadow: '0 4px 14px rgba(217,119,6,0.30)',
                }}
              >이 위치로 저장</button>
            </div>
          </div>
        </div>
      )}


      <div style={{ padding: '20px 16px 40px', maxWidth: '680px', margin: '0 auto' }}>

        {/* 날짜 + 현황 카드 */}
        <div style={{
          background: 'linear-gradient(135deg,#1e3a5f 0%,#1a56a0 60%,#2563eb 100%)',
          borderRadius: '24px',
          padding: '28px 28px 24px',
          color: '#fff',
          marginBottom: '16px',
          boxShadow: '0 8px 32px rgba(37,99,235,0.28)',
          animation: 'fadeUp 0.3s ease',
        }}>
          <div style={{ fontSize: '13px', opacity: 0.65, fontWeight: '600', marginBottom: '4px', letterSpacing: '0.04em' }}>
            {todayStr} ({weekDay})
          </div>
          <div style={{ fontSize: '22px', fontWeight: '900', letterSpacing: '-0.02em', marginBottom: '20px' }}>
            출퇴근 체크
          </div>

          {/* 출근/퇴근 시간 표시 */}
          <div style={{ display: 'flex', gap: '12px' }}>
            <div style={{
              flex: 1, background: 'rgba(255,255,255,0.12)', borderRadius: '16px',
              padding: '14px 16px', backdropFilter: 'blur(8px)',
            }}>
              <div style={{ fontSize: '11px', fontWeight: '700', opacity: 0.7, marginBottom: '6px', letterSpacing: '0.06em' }}>출근</div>
              <div style={{ fontSize: '26px', fontWeight: '900', letterSpacing: '-0.02em' }}>
                {fmtTime(todayStatus.last_go)}
              </div>
            </div>
            <div style={{
              flex: 1, background: 'rgba(255,255,255,0.12)', borderRadius: '16px',
              padding: '14px 16px', backdropFilter: 'blur(8px)',
            }}>
              <div style={{ fontSize: '11px', fontWeight: '700', opacity: 0.7, marginBottom: '6px', letterSpacing: '0.06em' }}>퇴근</div>
              <div style={{ fontSize: '26px', fontWeight: '900', letterSpacing: '-0.02em' }}>
                {fmtTime(todayStatus.last_leave)}
              </div>
            </div>
          </div>
          {(todayStatus.last_trip_start || todayStatus.last_trip_end) && (
            <div style={{
              marginTop: '10px', padding: '9px 14px', borderRadius: '12px',
              background: 'rgba(251,191,36,0.18)', border: '1px solid rgba(251,191,36,0.35)',
              display: 'flex', alignItems: 'center', gap: '14px', flexWrap: 'wrap',
              fontSize: '13px', fontWeight: '700',
            }}>
              <span style={{ opacity: 0.8 }}>🚗 당일출장</span>
              <span>출발 <b style={{ fontSize: '16px' }}>{fmtTime(todayStatus.last_trip_start)}</b></span>
              <span>복귀 <b style={{ fontSize: '16px' }}>{fmtTime(todayStatus.last_trip_end)}</b></span>
            </div>
          )}
        </div>

        {/* 알림 메시지 */}
        {msg && (
          <div style={{
            padding: '11px 16px', borderRadius: '12px', marginBottom: '12px',
            fontSize: '13px', fontWeight: '600', animation: 'fadeUp 0.2s ease',
            background: msg.type === 'success' ? '#d1fae5' : msg.type === 'warn' ? '#fefce8' : '#fce7f3',
            color: msg.type === 'success' ? '#065f46' : msg.type === 'warn' ? '#854d0e' : '#9d174d',
            border: `1px solid ${msg.type === 'success' ? '#a7f3d0' : msg.type === 'warn' ? '#fde68a' : '#fbcfe8'}`,
          }}>
            {msg.type === 'success' ? '✓ ' : msg.type === 'warn' ? '⚠ ' : '✕ '}{msg.text}
          </div>
        )}

        {/* GPS 상태 배너 */}
        {gpsStatus === 'checking' && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: '8px',
            padding: '10px 14px', borderRadius: '12px', marginBottom: '12px',
            background: '#f8fafc', border: '1px solid #e2e8f0',
            fontSize: '13px', color: '#64748b', fontWeight: '600',
          }}>
            <span style={{ animation: 'spin 1s linear infinite', display: 'inline-block' }}>⟳</span>
            GPS 상태 확인 중...
          </div>
        )}
        {(gpsStatus === 'denied' || gpsStatus === 'unavailable') && (
          <div style={{
            padding: '10px 14px', borderRadius: '12px', marginBottom: '12px',
            background: '#fce7f3', border: '1px solid #fbcfe8',
            fontSize: '13px', color: '#9d174d', fontWeight: '600',
          }}>
            {gpsStatus === 'denied'
              ? '⚠ 위치 권한이 거부되어 있습니다. 브라우저/기기 설정에서 위치 접근을 허용해 주세요.'
              : '⚠ 이 기기는 GPS를 지원하지 않아 출퇴근 체크를 사용할 수 없습니다.'}
          </div>
        )}

        {latestNotice?.seq ? (
          <div style={{
            padding: '11px 14px',
            borderRadius: 12,
            marginBottom: 12,
            background: '#eff6ff',
            border: '1px solid #bfdbfe',
            fontSize: 13,
            color: '#1e40af',
            fontWeight: 700,
            lineHeight: 1.5,
          }}
          >
            📢 최신 공지사항
            <div style={{ marginTop: 4, fontSize: 12, fontWeight: 600, color: '#2563eb' }}>
              {latestNotice.title}
            </div>
            <button
              type="button"
              onClick={() => openLatestNoticeModal(null)}
              style={{
                marginTop: 8,
                padding: '6px 10px',
                borderRadius: 8,
                border: '1px solid #93c5fd',
                background: '#fff',
                color: '#1d4ed8',
                fontSize: 12,
                fontWeight: 800,
                cursor: 'pointer',
              }}
            >
              공지 다시 보기
            </button>
          </div>
        ) : null}

        {/* 체크 버튼 */}
        {(() => {
          const gpsOk = gpsStatus === 'ok';
          const btnDisabled = checkLoading || !gpsOk;
          return (
            <div style={{ display: 'flex', gap: '10px', marginBottom: '24px' }}>
              <button className="att-check-btn" type="button"
                onClick={() => handleCheck('go')} disabled={btnDisabled}
                title={!gpsOk ? 'GPS 위치 권한이 필요합니다' : ''}
                style={{
                  flex: 1, padding: '16px 0',
                  background: btnDisabled
                    ? '#e2e8f0'
                    : 'linear-gradient(135deg,#064e3b,#047857)',
                  color: btnDisabled ? '#94a3b8' : '#fff',
                  border: 'none', borderRadius: '16px',
                  fontSize: '15px', fontWeight: '800',
                  cursor: btnDisabled ? 'not-allowed' : 'pointer',
                  boxShadow: btnDisabled ? 'none' : '0 4px 20px rgba(4,120,87,0.30)',
                  letterSpacing: '-0.01em',
                  transition: 'all 0.2s',
                }}
              >
                {checkLoading ? '⟳ 처리 중...' : !gpsOk ? '🔒 출근' : '출근'}
              </button>
              <button className="att-check-btn" type="button"
                onClick={() => handleCheck('leave')} disabled={btnDisabled}
                title={!gpsOk ? 'GPS 위치 권한이 필요합니다' : ''}
                style={{
                  flex: 1, padding: '16px 0',
                  background: btnDisabled
                    ? '#e2e8f0'
                    : 'linear-gradient(135deg,#450a0a,#9f1239)',
                  color: btnDisabled ? '#94a3b8' : '#fff',
                  border: 'none', borderRadius: '16px',
                  fontSize: '15px', fontWeight: '800',
                  cursor: btnDisabled ? 'not-allowed' : 'pointer',
                  boxShadow: btnDisabled ? 'none' : '0 4px 20px rgba(159,18,57,0.28)',
                  letterSpacing: '-0.01em',
                  transition: 'all 0.2s',
                }}
              >
                {checkLoading ? '⟳ 처리 중...' : !gpsOk ? '🔒 퇴근' : '퇴근'}
              </button>
            </div>
          );
        })()}

        {/* 당일출장 출발/복귀 */}
        {(() => {
          const gpsOk = gpsStatus === 'ok';
          const btnDisabled = checkLoading || !gpsOk;
          const tripBtn = (type, grad, shadow) => (
            <button className="att-check-btn" type="button"
              onClick={() => handleCheck(type)} disabled={btnDisabled}
              title={!gpsOk ? 'GPS 위치 권한이 필요합니다' : '당일출장 전용'}
              style={{
                flex: 1, padding: '12px 0',
                background: btnDisabled ? '#e2e8f0' : grad,
                color: btnDisabled ? '#94a3b8' : '#fff',
                border: 'none', borderRadius: '14px',
                fontSize: '14px', fontWeight: '800',
                cursor: btnDisabled ? 'not-allowed' : 'pointer',
                boxShadow: btnDisabled ? 'none' : shadow,
                transition: 'all 0.2s',
              }}
            >
              {checkLoading ? '⟳ 처리 중...' : `${!gpsOk ? '🔒 ' : '🚗 '}${attTypeMeta(type).label}`}
            </button>
          );
          return (
            <div style={{
              marginTop: '-14px', marginBottom: '24px', padding: '10px',
              background: '#fffbeb', border: '1px dashed #fcd34d', borderRadius: '16px',
            }}>
              <div style={{ fontSize: '11px', fontWeight: '700', color: '#92400e', margin: '0 2px 8px' }}>
                당일출장 전용 · 사무실에서 출장지로 나갈 때 <b>출장출발</b>, 돌아올 때 <b>출장복귀</b>
              </div>
              <div style={{ display: 'flex', gap: '10px' }}>
                {tripBtn('trip_start', 'linear-gradient(135deg,#92400e,#d97706)', '0 4px 16px rgba(217,119,6,0.28)')}
                {tripBtn('trip_end', 'linear-gradient(135deg,#4c1d95,#7c3aed)', '0 4px 16px rgba(124,58,237,0.28)')}
              </div>
              <div style={{ textAlign: 'right', marginTop: '8px' }}>
                <button type="button"
                  onClick={() => setTripManual({ type: todayStatus.last_trip_start && !todayStatus.last_trip_end ? 'trip_end' : 'trip_start', time: nowLocalInput() })}
                  style={{
                    padding: '5px 10px', borderRadius: '8px', border: '1px solid #fcd34d',
                    background: '#fff', color: '#92400e', fontSize: '12px', fontWeight: '700', cursor: 'pointer',
                  }}
                >
                  ⏱ 깜빡했어요 · 시간 직접 입력
                </button>
              </div>
            </div>
          );
        })()}

        {/* 로그 섹션 */}
        <div style={{
          background: '#fff', borderRadius: '20px',
          boxShadow: '0 2px 12px rgba(15,23,42,0.07)',
          overflow: 'hidden',
        }}>
          {/* 섹션 헤더 + 필터 */}
          <div style={{ padding: '16px 20px', borderBottom: '1px solid #f1f5f9' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: isAdmin ? '12px' : '0' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontWeight: '800', fontSize: '14px', color: '#0f172a' }}>기록</span>
                <span style={{
                  fontSize: '11px', fontWeight: '700', color: '#64748b',
                  background: '#f1f5f9', padding: '2px 8px', borderRadius: '99px',
                }}>
                  {loading ? '...' : total}
                </span>
              </div>
              <form onSubmit={handleSearch} style={{ display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap' }}>
                <QuickDateBar
                  mode={dateMode}
                  onMode={m => {
                    setDateMode(m);
                    if (m !== 'custom') {
                      const ago = m === 'this' ? 0 : parseInt(m);
                      const { from, to } = getMonthRange(ago);
                      setDateFrom(from);
                      setDateTo(to);
                    }
                  }}
                  dateFrom={dateFrom} dateTo={dateTo}
                  onFrom={setDateFrom} onTo={setDateTo}
                />
                <button type="submit" style={{
                  height: '30px', padding: '0 12px', background: '#2563eb', color: '#fff',
                  border: 'none', borderRadius: '8px', fontSize: '12px', fontWeight: '700', cursor: 'pointer',
                }}>조회</button>
              </form>
            </div>
          </div>

          {/* 특별수당 안내 */}
          <AllowanceGuide />

          {/* 테이블 */}
          <div style={{ overflowX: 'auto' }}>
            <table className="att-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#f8fafc' }}>
                  {isAdmin && <th style={thS}>이름</th>}
                  <th style={thS}>구분</th>
                  <th style={thS}>상태</th>
                  <th style={thS}>일시</th>
                  <th style={{ ...thS, textAlign: 'center', width: '44px' }}></th>
                </tr>
              </thead>
              <tbody>
                {logs.length === 0 ? (
                  <tr>
                    <td colSpan={isAdmin ? 5 : 4}
                      style={{ padding: '40px', textAlign: 'center', color: '#cbd5e1', fontSize: '13px' }}>
                      {loading ? '불러오는 중...' : '기록이 없습니다'}
                    </td>
                  </tr>
                ) : logs.map((row) => {
                  const st = row.status || 'normal';
                  const stBadge = {
                    normal:   { bg: '#f0fdf4', color: '#166534', label: '정상' },
                    pending:  { bg: '#fffbeb', color: '#92400e', label: '확인필요' },
                    approved: { bg: '#eff6ff', color: '#1e40af', label: '승인됨' },
                  }[st] || { bg: '#f1f5f9', color: '#64748b', label: st };
                  return (
                    <tr key={row.seq} className="att-row"
                      style={{ borderTop: '1px solid #f1f5f9', transition: 'background 0.1s' }}>
                      {isAdmin && (
                        <td className="att-td-name" style={{ ...tdS, fontWeight: '600', color: '#0f172a' }}>
                          {row.user_name || row.user_email?.split('@')[0]}
                        </td>
                      )}
                      <td className="att-td-type" style={tdS}>
                        <span style={{
                          display: 'inline-flex', alignItems: 'center', gap: '4px',
                          padding: '3px 10px', borderRadius: '99px',
                          fontWeight: '700', fontSize: '12px',
                          background: attTypeMeta(row.type).bg,
                          color: attTypeMeta(row.type).color,
                          whiteSpace: 'nowrap',
                        }}>
                          <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: attTypeMeta(row.type).dot, flexShrink: 0 }} />
                          {attTypeMeta(row.type).label}
                        </span>
                      </td>
                      <td className="att-td-status" style={tdS}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'nowrap' }}>
                          <span style={{
                            padding: '2px 9px', borderRadius: '99px', fontSize: '11px', fontWeight: '700',
                            background: stBadge.bg, color: stBadge.color, whiteSpace: 'nowrap',
                          }}>
                            {stBadge.label}
                          </span>
                          {st === 'approved' && row.approved_by && (
                            <span style={{ fontSize: '10px', color: '#94a3b8', whiteSpace: 'nowrap' }}>
                              {row.approved_by}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="att-td-dt" style={{ ...tdS, color: '#475569', fontVariantNumeric: 'tabular-nums' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                          <span>{fmtDatetime(row.created_at)}</span>
                          {row.type === 'go' && (allowances[row.seq] || []).map(({ tag, amount }) => (
                            <span key={tag} style={{
                              display: 'inline-flex', alignItems: 'center', gap: '3px',
                              fontSize: '11px', fontWeight: '700', whiteSpace: 'nowrap',
                              background: tag === '새벽수당' ? '#1e1b4b' : tag === '외박수당' ? '#164e63' : '#134e4a',
                              color: tag === '새벽수당' ? '#c7d2fe' : tag === '외박수당' ? '#a5f3fc' : '#99f6e4',
                              borderRadius: '99px', padding: '2px 7px',
                            }}>
                              {tag === '새벽수당' ? '🌙' : tag === '외박수당' ? '🏨' : '🌙🌙'} {tag}
                              {amount != null && amount !== 0 && <span style={{ opacity: 0.85 }}> +{Number(amount).toLocaleString()}</span>}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td className="att-td-pin" style={{ ...tdS, textAlign: 'center', padding: '8px 12px' }}>
                        {row.lat != null && row.lng != null ? (
                          <button className="att-pin-btn"
                            onClick={() => setMapTarget({
                              lat: row.lat, lng: row.lng,
                              label: `${row.user_name || row.user_email} · ${fmtDatetime(row.created_at)} · ${attTypeMeta(row.type).label}`,
                            })}
                            style={{
                              width: '28px', height: '28px', borderRadius: '8px',
                              border: '1px solid #e0f2fe', background: '#f0f9ff',
                              color: '#0369a1', fontSize: '15px',
                              cursor: 'pointer', display: 'inline-flex',
                              alignItems: 'center', justifyContent: 'center',
                              transition: 'background 0.15s',
                            }}
                            title="위치 보기"
                          >📍</button>
                        ) : (
                          <span style={{ color: '#e2e8f0', fontSize: '13px' }}>—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* 페이지네이션 */}
          {totalPages > 1 && (
            <div style={{
              display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '6px',
              padding: '12px', borderTop: '1px solid #f1f5f9',
            }}>
              <button onClick={() => handlePageChange(Math.max(0, page - 1))} disabled={page === 0} style={pageBtnS(page === 0)}>‹</button>
              <span style={{ padding: '4px 12px', fontSize: '13px', color: '#64748b', fontWeight: '600' }}>{page + 1} / {totalPages}</span>
              <button onClick={() => handlePageChange(Math.min(totalPages - 1, page + 1))} disabled={page >= totalPages - 1} style={pageBtnS(page >= totalPages - 1)}>›</button>
            </div>
          )}
        </div>
      </div>

      {/* 네이버 지도 모달 */}
      {mapTarget && (
        <NaverMapModal
          lat={mapTarget.lat}
          lng={mapTarget.lng}
          label={mapTarget.label}
          onClose={() => setMapTarget(null)}
        />
      )}
    </div>
  );
}
